// Custom Chakra UI theme with orange as primary color
// Supports both light and dark modes

export const colors = {
  brand: {
    // Orange palette for primary brand color
    50: '#fff5f0',
    100: '#ffe4d6',
    200: '#ffc9ad',
    300: '#ffad85',
    400: '#ff925c',
    500: '#f4885b', // Main brand orange (from logo)
    600: '#e6703d',
    700: '#c85a2a',
    800: '#a4461a',
    900: '#7d3310'
  }
};

// Replicated COLORS object from colors.js for compatibility with legacy components
export const COLORS = {
  brand: {
    primary: 'var(--chakra-colors-orange-500)',
    hover: 'var(--chakra-colors-orange-600)',
    light: 'var(--chakra-colors-orange-400)',
    dark: 'var(--chakra-colors-orange-700)'
  },
  brandHex: {
    primary: '#f4885b',
    hover: '#e6703d',
    light: '#ff925c',
    dark: '#c85a2a'
  },
  gradients: {
    hero: {
      light: 'linear-gradient(135deg, #fff5f0 0%, #ffe4d6 50%, #ffffff 100%)',
      dark: 'linear-gradient(135deg, #2d1810 0%, #1d0531 50%, #0a0a0a 100%)'
    },
    neutral: {
      light: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
      dark: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
    }
  },
  glass: {
    bg: {
      light: 'rgba(255, 255, 255, 0.85)',
      dark: 'rgba(15, 15, 15, 0.7)'
    },
    border: {
      light: 'rgba(244, 136, 91, 0.2)',
      dark: 'rgba(255, 255, 255, 0.1)'
    },
    overlay: {
      light: 'rgba(255, 255, 255, 0.45)',
      dark: 'rgba(0, 0, 0, 0.4)'
    },
    blur: '16px',
    shadow: {
      light: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
      dark: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
    }
  }
};

export const semanticTokens = {
  colors: {
    // Primary brand colors
    'brand.primary': {
      _light: 'brand.500',
      _dark: 'brand.400'
    },
    'brand.hover': {
      _light: 'brand.600',
      _dark: 'brand.300'
    },
    'brand.active': {
      _light: 'brand.700',
      _dark: 'brand.200'
    },

    // Background colors
    'bg.primary': {
      _light: 'white',
      _dark: 'black'
    },
    'bg.secondary': {
      _light: 'gray.50',
      _dark: 'gray.900'
    },
    'bg.hero': {
      _light: 'linear-gradient(to bottom right, #fff5f0, #ffffff)',
      _dark: 'linear-gradient(to bottom right, #2d1810, #1d0531)'
    },

    // Text colors
    'text.primary': {
      _light: 'gray.900',
      _dark: 'white'
    },
    'text.secondary': {
      _light: 'gray.600',
      _dark: 'gray.400'
    },
    'text.muted': {
      _light: 'gray.500',
      _dark: 'gray.500'
    },

    // Border colors
    'border.primary': {
      _light: 'brand.500',
      _dark: 'brand.400'
    },
    'border.secondary': {
      _light: 'gray.200',
      _dark: 'gray.700'
    },

    // Glassmorphism tokens
    'glass.bg': {
      _light: 'rgba(255, 255, 255, 0.85)',
      _dark: 'rgba(15, 15, 15, 0.7)'
    },
    'glass.border': {
      _light: 'rgba(244, 136, 91, 0.2)',
      _dark: 'rgba(255, 255, 255, 0.1)'
    },
    'glass.shadow': {
      _light: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
      _dark: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
    }
  }
};

export const breakpoints = {
  base: '0px',
  sm: '320px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px'
};

// Component style overrides
export const components = {
  Button: {
    defaultProps: {
      colorScheme: 'brand'
    },
    variants: {
      solid: {
        bg: 'brand.primary',
        color: 'white',
        _hover: {
          bg: 'brand.hover',
          transform: 'translateY(-2px)',
          shadow: 'lg'
        },
        _active: {
          bg: 'brand.active'
        }
      },
      ghost: {
        color: 'brand.primary',
        _hover: {
          bg: { _light: 'brand.50', _dark: 'whiteAlpha.200' }
        }
      },
      outline: {
        borderColor: 'brand.primary',
        color: 'brand.primary',
        _hover: {
          bg: { _light: 'brand.50', _dark: 'whiteAlpha.200' }
        }
      }
    }
  },
  IconButton: {
    defaultProps: {
      colorScheme: 'brand'
    }
  },
  Badge: {
    defaultProps: {
      colorScheme: 'brand'
    }
  },
  Link: {
    baseStyle: {
      color: 'brand.primary',
      _hover: {
        color: 'brand.hover',
        textDecoration: 'none'
      }
    }
  }
};

// Typography
export const fonts = {
  heading:
    "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
  cursive: "'Allura', cursive"
};

// Global styles
export const styles = {
  global: {
    body: {
      bg: 'bg.primary',
      color: 'text.primary'
    },
    '*::placeholder': {
      color: 'text.muted'
    },
    '*, *::before, *::after': {
      borderColor: 'border.secondary'
    }
  }
};

export const theme = {
  colors,
  semanticTokens,
  breakpoints,
  components,
  fonts,
  styles
};

// Helper function to get brand color with fallback
export const getBrandColor = (shade = 'primary') => {
  return COLORS.brand[shade] || COLORS.brand.primary;
};

export const getBrandColorHex = (shade = 'primary') => {
  return COLORS.brandHex[shade] || COLORS.brandHex.primary;
};

export default theme;
